import React, { useEffect } from 'react';
import { 
  getAffiliateFromUrl, 
  trackAffiliateClick, 
  storeSecureAffiliateSession,
  getSecureAffiliateSession,
  getAffiliateIdFromCode,
  getAffiliate,
  generateSessionId,
  generateSecureTrackingToken,
  isInTestingMode
} from '../firebase/affiliateService';

interface AffiliateTrackerProps {
  children: React.ReactNode;
}

const AffiliateTracker: React.FC<AffiliateTrackerProps> = ({ children }) => {
  const initializationStarted = React.useRef(false);
  const clickTracked = React.useRef(false);

  useEffect(() => {
    // Prevent duplicate execution in React Strict Mode
    if (initializationStarted.current) {
      return;
    }

    const sessionKey = 'affiliate_tracker_initialized';
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    initializationStarted.current = true;
    sessionStorage.setItem(sessionKey, 'true');

    const initializeAffiliateTracking = async () => {
      try {
        // Get affiliate code from URL
        const affiliateCode = getAffiliateFromUrl();
        
        if (affiliateCode) {
          // Convert affiliate code to affiliate ID
          const affiliateId = await getAffiliateIdFromCode(affiliateCode);
          
          if (!affiliateId) {
            return;
          }
          
          // Get affiliate data to verify status
          const affiliate = await getAffiliate(affiliateId);
          
          const testingMode = isInTestingMode();
          
          if (affiliate && (affiliate.status === 'active' || testingMode)) {
            // Generate session ID and tracking token
            const sessionId = generateSessionId();
            const trackingToken = generateSecureTrackingToken(affiliateId, sessionId);
            
            // ALWAYS store the session data when an affiliate is found
            // This ensures conversion tracking will work regardless of click tracking
            await storeSecureAffiliateSession(affiliateId, sessionId, trackingToken);
            
            // Enhanced duplicate prevention using multiple mechanisms
            const trackingKey = `affiliate_tracking_${affiliateCode}_${sessionId}`;
            const globalTrackingKey = `affiliate_tracking_${affiliateCode}`;
            const alreadyTracked = localStorage.getItem(trackingKey) || localStorage.getItem(globalTrackingKey);
            const sessionTracked = sessionStorage.getItem(trackingKey);
            
            // Only track if not already tracked in any way
            if (!alreadyTracked && !sessionTracked && !clickTracked.current) {
              // Mark as tracked immediately to prevent duplicates
              clickTracked.current = true;
              localStorage.setItem(trackingKey, Date.now().toString());
              sessionStorage.setItem(trackingKey, 'true');
              
              // Track the click
              await trackAffiliateClick(affiliateId, sessionId, {
                trackingToken: trackingToken
              });
              
              // Clean up old tracking flags (older than 1 hour)
              const oneHourAgo = Date.now() - (60 * 60 * 1000);
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('affiliate_tracking_')) {
                  const timestamp = localStorage.getItem(key);
                  if (timestamp && parseInt(timestamp) < oneHourAgo) {
                    localStorage.removeItem(key);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('AffiliateTracker: Error initializing affiliate tracking', error);
      }
    };

    initializeAffiliateTracking();

    // Cleanup function
    return () => {
      sessionStorage.removeItem(sessionKey);
      initializationStarted.current = false;
      clickTracked.current = false;
    };
  }, []);

  // Add debugging function to window for testing
  React.useEffect(() => {
    (window as any).debugAffiliateSession = () => {
      const session = getSecureAffiliateSession();
      return session;
    };
    
    (window as any).testCreateSession = (affiliateId: string) => {
      import('../firebase/affiliateService').then(({ testCreateSession }) => {
        testCreateSession(affiliateId);
      });
    };
    
    (window as any).createSessionForAffiliate = (affiliateId: string) => {
      import('../firebase/affiliateService').then(({ 
        generateSessionId, 
        generateSecureTrackingToken, 
        storeSecureAffiliateSession 
      }) => {
        const sessionId = generateSessionId();
        const trackingToken = generateSecureTrackingToken(affiliateId, sessionId);
        
        try {
          storeSecureAffiliateSession(affiliateId, sessionId, trackingToken);
        } catch (error) {
          console.error('Error creating session:', error);
        }
      });
    };
    
    (window as any).listAllAffiliates = () => {
      import('../firebase/affiliateService').then(({ listAllAffiliates }) => {
        listAllAffiliates();
      });
    };
    
    (window as any).createTestAffiliate = () => {
      import('../firebase/affiliateService').then(({ createTestAffiliate }) => {
        createTestAffiliate().catch(error => {
          console.error('Failed to create test affiliate:', error);
        });
      });
    };

    (window as any).clearAllAffiliateData = () => {
      // Clear localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('app_') || key.startsWith('affiliate_') || key.startsWith('user_')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('affiliate') || key.includes('tracker')) {
          sessionStorage.removeItem(key);
        }
      });
    };
    
    (window as any).createSessionForCurrentAffiliate = async (affiliateId?: string) => {
      import('../firebase/affiliateService').then(async ({ 
        generateSessionId, 
        generateSecureTrackingToken, 
        storeSecureAffiliateSession,
        getAffiliateIdFromCode
      }) => {
        let targetAffiliateId = affiliateId;
        
        // If no affiliate ID provided, try to get from URL
        if (!targetAffiliateId) {
          const urlParams = new URLSearchParams(window.location.search);
          const partnerParam = urlParams.get('partner');
          
          if (!partnerParam) {
            return;
          }
          
          // Convert affiliate code to affiliate ID
          const affiliateIdResult = await getAffiliateIdFromCode(partnerParam);
          targetAffiliateId = affiliateIdResult || undefined;
          
          if (!targetAffiliateId) {
            return;
          }
        }
        
        const sessionId = generateSessionId();
        const trackingToken = generateSecureTrackingToken(targetAffiliateId, sessionId);
        
        try {
          storeSecureAffiliateSession(targetAffiliateId, sessionId, trackingToken);
        } catch (error) {
          console.error('Error creating session:', error);
        }
      });
    };
  }, []);

  return <>{children}</>;
};

export default AffiliateTracker;

